import React, { useEffect, useMemo, useRef } from 'react';

import { cn } from '@/utils';

import { formatElapsed, type SpeechSegment } from '../utils/richTranscript';

interface SyncedTranscriptProps {
  segments: SpeechSegment[];
  currentTime: number;
  onSeek: (seconds: number) => void;
}

/** Index of the segment playing at `time` (latest start wins on overlap), or -1. */
export function activeSegmentIndex(segments: SpeechSegment[], time: number): number {
  let found = -1;
  segments.forEach((s, i) => {
    if (s.start <= time && time < s.end) found = i;
  });
  return found;
}

/** Speech bubbles that follow the recording; clicking one seeks to it. */
export const SyncedTranscript: React.FC<SyncedTranscriptProps> = ({
  segments,
  currentTime,
  onSeek,
}) => {
  const active = useMemo(() => activeSegmentIndex(segments, currentTime), [segments, currentTime]);
  const activeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [active]);

  if (segments.length === 0) {
    return <p className="p-4 text-sm text-muted-foreground">No speech recorded.</p>;
  }

  return (
    <div className="flex flex-col gap-2 p-2">
      {segments.map((s, i) => (
        <button
          key={`${s.start}-${i}`}
          ref={i === active ? activeRef : undefined}
          type="button"
          onClick={() => onSeek(s.start)}
          className={cn(
            'max-w-[80%] rounded-lg px-3 py-2 text-left text-sm',
            s.speaker === 'agent' ? 'self-end bg-muted' : 'self-start border border-border',
            i === active && 'ring-2 ring-primary',
          )}
        >
          <span className="block text-xs text-muted-foreground">
            {s.speaker === 'agent' ? 'Agent' : 'Caller'} · {formatElapsed(s.start)}
          </span>
          {s.text}
        </button>
      ))}
    </div>
  );
};
